import React, { forwardRef, useMemo } from 'react';
import classNames from 'classnames';
import { Property } from 'csstype';
import { useTheme } from 'styled-components';

import { Theme } from '@minecraft.themes';
import {
  computeBorderStyle,
  computeContainerStyle,
  computeBorderRadiusStyle,
  computeOffsetStyle,
  getGridItemStyles,
  getMarginClassnames,
  getWidthClassnames,
  getHeightClassnames,
} from '../helpers';
import { BorderProps, ContainerStyleProps, FlexProps, FlexStyleProps, HeightStyleProps } from '../types';

const getDisplayValue = ({
  setDisplayContents,
  hidden,
  setDisplayInline,
}: {
  setDisplayContents?: boolean;
  hidden?: boolean;
  setDisplayInline?: boolean;
}) => {
  if (hidden) {
    return 'none';
  }
  if (setDisplayContents) {
    return 'contents';
  }
  if (setDisplayInline) {
    return 'inline-flex';
  }
  return 'flex';
};

const getMinHeightStyle = ({ minHeight, minH = '' }: { minHeight?: number | string; minH?: string }) => {
  // if the minHeight is set by classname, we want to ignore the minHeight prop and let the className handle it
  if (minH) {
    return undefined;
  }

  return typeof minHeight === 'number' ? `${minHeight}rem` : '1px';
};

const getFlexStyles = ({
  alignContent,
  alignItems = 'flex-start',
  bgColor,
  borderBottom = false,
  borderColor,
  borderPlacement = 'none',
  borderSize = 0.06,
  borderStyle,
  customBorders,
  direction = 'row',
  hidden = false,
  justifyContent = 'flex-start',
  justifyItems,
  maxHeight,
  maxWidth,
  minHeight,
  minWidth,
  reverse = false,
  textColor,
  theme,
  wrap = 'wrap',
  customBordersRadius,
  top,
  right,
  bottom,
  left,
  bgOpacity = 1,
  backgroundImage,
  setDisplayContents = false,
  bgBlur,
  setDisplayInline,
  minH,
  isScrollbar = false,
}: FlexStyleProps & BorderProps & ContainerStyleProps & HeightStyleProps & { theme: Theme; className?: string }) => {
  let flexDirection: Property.FlexDirection = direction;
  const display = getDisplayValue({ setDisplayContents, hidden, setDisplayInline });
  if (reverse) {
    flexDirection = direction === 'row' ? 'row-reverse' : 'column-reverse';
  }

  return {
    alignContent,
    alignItems,
    color: theme.designSystem.textColors[textColor],
    display,
    flexDirection,
    flexWrap: wrap,
    justifyContent,
    justifyItems,
    maxHeight: typeof maxHeight === 'number' ? `${maxHeight}rem` : undefined,
    maxWidth: typeof maxWidth === 'number' ? `${maxWidth}rem` : undefined,
    minHeight: getMinHeightStyle({ minHeight, minH }),
    minWidth: typeof minWidth === 'number' ? `${minWidth}rem` : '1px',
    ...computeContainerStyle({ theme, textColor, bgColor, bgOpacity, backgroundImage, bgBlur }),
    ...computeBorderStyle({
      borderPlacement,
      borderSize,
      borderColor,
      borderBottom,
      borderStyle,
      customBorders,
      theme,
    }),
    ...computeBorderRadiusStyle({
      customBordersRadius,
    }),
    ...computeOffsetStyle({
      top,
      right,
      bottom,
      left,
    }),
    ...(isScrollbar
      ? {
          scrollbarColor: `${theme.designSystem.bgColors.scrollbarThumb} transparent`,
          scrollbarWidth: 'thin' as const,
        }
      : {}),
  };
};

export const FlexBase = forwardRef<HTMLDivElement, FlexProps>(
  (
    {
      children,
      id,
      className,
      qaId,
      'data-testid': dataTestId,
      'data-qa-id': dataQaId,
      gap = '0',
      grow = 1,
      shrink = 1,
      borderRadius = 'none',
      role,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      zIndex,
      padding,
      ...styleProps
    },
    ref
  ) => {
    const theme = useTheme() as Theme;
    const style = useMemo(
      () =>
        getFlexStyles({
          ...styleProps,
          theme,
        }),
      [styleProps, theme]
    );

    const gridItemStyle = useMemo(
      () => ({
        ...getGridItemStyles({ ...styleProps, theme }),
        theme,
      }),
      [styleProps, theme]
    );

    const qaIdValue = qaId || dataQaId;
    const optionalProps = useMemo(() => {
      return {
        ...(typeof dataTestId === 'string' && dataTestId ? { 'data-testid': dataTestId } : {}),
        ...(typeof qaIdValue === 'string' && qaIdValue ? { 'data-qa-id': qaIdValue } : {}),
      };
    }, [dataTestId, qaIdValue]);

    return (
      <div
        id={id}
        className={classNames(
          {
            [`cn_atom_zIndex-${zIndex}`]: zIndex !== undefined,
            [`cn_atom_gap-${gap}`]: true,
            [`cn_atom_grow-${grow}`]: true,
            [`cn_atom_shrink-${shrink}`]: true,
            [`cn_atom_rounded-${borderRadius}`]: !styleProps.customBordersRadius,
            [`cn_atom_p-${padding}`]: !!padding,
          },
          getMarginClassnames(styleProps),
          getWidthClassnames(styleProps),
          getHeightClassnames(styleProps),
          className
        )}
        {...optionalProps}
        style={{ ...gridItemStyle, ...style }}
        ref={ref}
        role={role}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        {children}
      </div>
    );
  }
);
